import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookingInfoDto,
  BookingInfoPeriodDefinition,
  BookingInfoWeekDetailDto,
  BookingInfoWeekDto,
} from "../../../graphql/models/Booking";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { AddOutlined, ExpandMoreOutlined } from "@material-ui/icons";
import { BookingWeek } from "./booking_week";
import { Alert } from "@material-ui/lab";
import { BookingClosureCollection } from "./booking_closure";
import { DateTime } from "luxon";
import { useDateTimeUtils } from "../../../hooks";
import { TextFieldArray } from "../textfield_array";

export enum BookingInfoServiceTypeEnum {
  AMENITY = 10,
  SERVICE = 20,
}
export interface BookingInfoProps {
  value: BookingInfoDto;
  onChange: (value: BookingInfoDto) => void;
  type: BookingInfoServiceTypeEnum;
}

export function BookingInfo(props: BookingInfoProps) {
  const { value, type } = props;
  const [closingDays, setClosingDays] = useState(value?.closingDays ?? []);
  const [intervalBetweenStartsInMinutes, setIntervalBetweenStartsInMinutes] =
    useState(value?.intervalBetweenStartsInMinutes ?? 0);
  const [periods, setPeriods] = useState(value?.periods ?? []);
  const [advance, setAdvance] = useState(value?.defaultAdvanceInMinutes ?? 0);
  const [useServiceItemDuration, setUseServiceItemDuration] = useState(
    value?.useServiceItemDuration ?? false
  );
  const [durations, setDurations] = useState(
    value?.durations ? value.durations : [0]
  );
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceIndex, setCloneSourceIndex] = useState<number | null>(null);
  const [cloneTargetIsoDate, setCloneTargetIsoDate] = useState<string | null>(
    null
  );
  console.debug(`periods length is ${periods.length}`);

  const showServiceFields = useMemo(
    () => type === BookingInfoServiceTypeEnum.SERVICE,
    [type]
  );

  const emptyPeriod = useMemo(() => {
    console.debug(`useMemo emptyPeriod`);
    const defaultDay: BookingInfoWeekDetailDto = {
      maxUnits: 0,
      ranges: [],
      allDay: false,
    };
    const weekSlots: BookingInfoWeekDto = {
      monday: { ...defaultDay },
      tuesday: { ...defaultDay },
      wednesday: { ...defaultDay },
      thursday: { ...defaultDay },
      friday: { ...defaultDay },
      saturday: { ...defaultDay },
      sunday: { ...defaultDay },
    };
    const period: BookingInfoPeriodDefinition = {
      fromDateInIso: DateTime.now().toISODate(),
      weekSlots,
      isClosed: false,
    };
    return period;
  }, []);

  const { displayDate, datePickerValueToIsoDate } = useDateTimeUtils();

  const reduceDurations = useCallback(
    (array) => array.filter((v) => v !== 0),
    []
  );

  // handle number changes
  const handleNumberChange = (value: string, key: string, index?: number) => {
    const n = parseInt(value);
    if (isNaN(n)) return;
    switch (key) {
      case "interval":
        setIntervalBetweenStartsInMinutes(n);
        break;
      case "advance":
        setAdvance(n);
        break;
      case "duration":
        const _d = [...durations];
        _d.splice(index as number, 1, n);
        setDurations(_d);
        break;
    }
  };

  const handlePeriodChange = (
    value: BookingInfoPeriodDefinition,
    index: number
  ) => {
    console.debug(`handlePeriodChange(${index})`);
    const newArray = [...periods];
    newArray.splice(index, 1, value);
    setPeriods(newArray);
  };

  const handleNewPeriod = () => {
    console.debug(`handleNewPriod`);
    setPeriods([...periods, { ...emptyPeriod }]);
  };

  const openCloneDialog = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setCloneSourceIndex(index);
    setCloneTargetIsoDate(null);
    setCloneDialogOpen(true);
  };

  const closeCloneDialog = () => {
    setCloneDialogOpen(false);
    setCloneSourceIndex(null);
    setCloneTargetIsoDate(null);
  };

  const isCloneDateDuplicate = (isoDate: string) =>
    periods.some((period) => period.fromDateInIso === isoDate);

  const handleCloneConfirm = () => {
    if (cloneSourceIndex === null || !cloneTargetIsoDate) return;
    if (isCloneDateDuplicate(cloneTargetIsoDate)) return;

    const source = periods[cloneSourceIndex];
    if (!source) return;

    const cloned: BookingInfoPeriodDefinition = JSON.parse(
      JSON.stringify(source)
    );
    cloned.fromDateInIso = cloneTargetIsoDate;

    setPeriods([...periods, cloned]);
    closeCloneDialog();
  };

  const handleCloneDateChange = (value: MaterialUiPickersDate) => {
    const iso = datePickerValueToIsoDate(value);
    setCloneTargetIsoDate(iso ?? null);
  };

  const handleDurationsChange = (value: (string | number)[]) => {
    const v = value as number[];
    if (v) {
      setDurations(v.sort((prev, next) => prev - next));
    }
  };

  const checkMinutesValue = useCallback((v) => v % 5 === 0, []);

  // check for correct values
  const [alertDurations, alertInterval] = useMemo(() => {
    console.debug(`useMemo [alerts]`);
    const alertInterval =
      intervalBetweenStartsInMinutes === 0 ||
      !checkMinutesValue(intervalBetweenStartsInMinutes);
    const alertDurations = durations.map((item) => {
      if (!checkMinutesValue(item))
        return "Deve essere un multiplo di 5 minuti";
      return "";
    });
    return [alertDurations, alertInterval];
  }, [intervalBetweenStartsInMinutes, durations, checkMinutesValue]);

  const effectiveDurations = useMemo(
    () => reduceDurations(durations),
    [durations, reduceDurations]
  );

  useEffect(() => {
    console.debug(`useEffect propagation, periods length is ${periods.length}`);
    const newValue: BookingInfoDto = {
      durations: effectiveDurations,
      intervalBetweenStartsInMinutes,
      periods,
      closingDays,
      defaultAdvanceInMinutes: advance,
      useServiceItemDuration,
    };
    console.debug(
      `new bookingInfo value is `,
      JSON.parse(JSON.stringify(newValue))
    );

    props.onChange(newValue);
  }, [
    effectiveDurations,
    intervalBetweenStartsInMinutes,
    periods,
    closingDays,
    advance,
    useServiceItemDuration,
  ]);

  return (
    <Grid item xs={12}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreOutlined />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>Impostazioni Slot: configurazione</Typography>
          <Button
            style={{ marginLeft: "10px" }}
            variant="contained"
            onClick={handleNewPeriod}
            startIcon={<AddOutlined fontSize="inherit" />}
          >
            Aggiungi un nuovo periodo di definizione orari
          </Button>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item md={4} lg={2}>
              <TextField
                type="number"
                label="Intervallo fra due slot (minuti)"
                variant={"outlined"}
                style={{ width: "100%" }}
                value={intervalBetweenStartsInMinutes}
                onChange={(e) => handleNumberChange(e.target.value, "interval")}
              />
              {alertInterval && (
                <Alert severity="error">
                  Deve essere un multiplo di 5 minuti
                </Alert>
              )}
            </Grid>
            <Grid item md={4} lg={2}>
              <TextField
                type="number"
                label="Anticipo standard prenotazione (in minuti)"
                variant={"outlined"}
                style={{ width: "100%" }}
                value={advance}
                onChange={(e) => handleNumberChange(e.target.value, "advance")}
              />
              {alertInterval && (
                <Alert severity="error">
                  Deve essere un multiplo di 5 minuti
                </Alert>
              )}
            </Grid>
            <Grid item md={4} lg={8}></Grid>
            {showServiceFields && (
              <Grid item xs={12}>
                <Checkbox
                  checked={useServiceItemDuration}
                  onChange={(e, checked) => setUseServiceItemDuration(checked)}
                ></Checkbox>{" "}
                Usa la durata indicata sui servizi
              </Grid>
            )}
            <TextFieldArray
              disabled={useServiceItemDuration}
              type="number"
              label="Durata"
              variant={"outlined"}
              values={durations}
              onArrayChange={handleDurationsChange}
              alerts={alertDurations}
              lg={2}
            />
          </Grid>
        </AccordionDetails>
      </Accordion>
      {periods.map((period, index) => {
        const { fromDateInIso } = period;
        return (
          <Accordion key={`period_${index}`}>
            <AccordionSummary
              expandIcon={<ExpandMoreOutlined />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography>
                Impostazioni Slot: dati settimanali (dal{" "}
                {displayDate(fromDateInIso)})
              </Typography>
              <Button
                style={{ marginLeft: "10px" }}
                variant="outlined"
                size="small"
                onClick={(event) => openCloneDialog(index, event)}
              >
                Clona
              </Button>
            </AccordionSummary>
            <AccordionDetails>
              <BookingWeek
                value={period}
                onChange={(v) => handlePeriodChange(v, index)}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreOutlined />}
          aria-controls="panel2a-content"
          id="panel2a-header"
        >
          <Typography>Impostazioni Slot: giorni di chiusura</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <BookingClosureCollection
            value={closingDays}
            onChange={setClosingDays}
          />
        </AccordionDetails>
      </Accordion>
      <Dialog
        open={cloneDialogOpen}
        onClose={closeCloneDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Clona periodo</DialogTitle>
        <DialogContent>
          <DatePicker
            label="Data di inizio del nuovo periodo"
            value={cloneTargetIsoDate}
            inputVariant="outlined"
            onChange={handleCloneDateChange}
            format="dd/MM/yyyy"
            style={{ width: "100%", marginTop: 8 }}
            clearable
          />
          {cloneTargetIsoDate && isCloneDateDuplicate(cloneTargetIsoDate) && (
            <Alert severity="error" style={{ marginTop: 8 }}>
              Esiste già un periodo con questa data di inizio
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCloneDialog}>Annulla</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={
              !cloneTargetIsoDate || isCloneDateDuplicate(cloneTargetIsoDate)
            }
            onClick={handleCloneConfirm}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
